
export interface AddressFormat {
    country: string;
    code: string;
    format: (data: any) => string;
    generate: () => any;
}

const getRandomItem = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

// Data Sets
const TR_CITIES = [
    { name: "İstanbul", districts: ["Kadıköy", "Beşiktaş", "Şişli", "Üsküdar", "Maltepe", "Ataşehir", "Beyoğlu", "Fatih"] },
    { name: "Ankara", districts: ["Çankaya", "Keçiören", "Yenimahalle", "Mamak", "Etimesgut", "Sincan", "Altındağ"] },
    { name: "İzmir", districts: ["Konak", "Karşıyaka", "Bornova", "Buca", "Çiğli", "Gaziemir", "Karabağlar"] },
    { name: "Antalya", districts: ["Muratpaşa", "Kepez", "Konyaaltı", "Alanya", "Manavgat"] },
    { name: "Bursa", districts: ["Nilüfer", "Osmangazi", "Yıldırım", "İnegöl"] }
];

const TR_STREETS = ["Atatürk Cad.", "Cumhuriyet Cad.", "İnönü Cad.", "Bağdat Cad.", "İstiklal Cad.", "Gazi Mustafa Kemal Bulvarı", "Barış Sok.", "Karanfil Sok.", "Lale Sok.", "Gül Sok.", "Menekşe Sok."];
const TR_NEIGHBORHOODS = ["Cumhuriyet Mah.", "Hürriyet Mah.", "İstiklal Mah.", "Fatih Mah.", "Yeni Mah.", "Merkez Mah.", "Bahçelievler Mah.", "Gazi Mah."];

const US_STATES = [
    { name: "California", abr: "CA", cities: ["Los Angeles", "San Francisco", "San Diego", "Sacramento", "San Jose"] },
    { name: "New York", abr: "NY", cities: ["New York City", "Buffalo", "Albany", "Rochester", "Yonkers"] },
    { name: "Texas", abr: "TX", cities: ["Houston", "Austin", "Dallas", "San Antonio", "Fort Worth"] },
    { name: "Florida", abr: "FL", cities: ["Miami", "Orlando", "Tampa", "Jacksonville", "Tallahassee"] },
    { name: "Illinois", abr: "IL", cities: ["Chicago", "Springfield", "Aurora", "Naperville", "Joliet"] }
];

const US_STREETS = ["Main St", "Broadway", "Market St", "Pine St", "Maple Ave", "Oak St", "Washington St", "Park Ave", "Sunset Blvd", "High St", "2nd Ave", "Chestnut St"];

const DE_CITIES = [
    { name: "Berlin", zips: ["10115", "10117", "10119", "10178", "10179"] },
    { name: "München", zips: ["80331", "80333", "80335", "80336", "80337"] },
    { name: "Hamburg", zips: ["20095", "20097", "20099", "20144", "20146"] },
    { name: "Frankfurt", zips: ["60311", "60313", "60314", "60316", "60318"] },
    { name: "Köln", zips: ["50667", "50668", "50670", "50672", "50674"] }
];
const DE_STREETS = ["Hauptstraße", "Bahnhofstraße", "Dorfstraße", "Bergstraße", "Schulstraße", "Gartenstraße", "Ringstraße", "Waldstraße", "Feldstraße"];

const UK_CITIES = ["London", "Manchester", "Birmingham", "Leeds", "Glasgow", "Liverpool", "Bristol", "Edinburgh"];
const UK_STREETS = ["High Street", "Station Road", "London Road", "Main Street", "Church Street", "Park Road", "Victoria Road", "The Avenue", "Queens Road"];

interface GeneratedAddress {
    fullAddress: string;
    city: string;
    district?: string;
    state?: string;
    street: string;
    zip: string | number;
    country: string;
}

// Generators
export const ADDRESS_GENERATORS: Record<string, { generate: () => GeneratedAddress }> = {
    TR: {
        generate: () => {
            const cityData = getRandomItem(TR_CITIES);
            const district = getRandomItem(cityData.districts);
            const neighborhood = getRandomItem(TR_NEIGHBORHOODS);
            const street = getRandomItem(TR_STREETS);
            const buildingNo = getRandomNumber(1, 150);
            const doorNo = getRandomNumber(1, 20);
            const zip = getRandomNumber(10000, 81999);

            return {
                fullAddress: `${neighborhood} ${street} No:${buildingNo} D:${doorNo}\n${district}/${cityData.name} ${zip}`,
                city: cityData.name,
                district,
                street,
                zip,
                country: "Türkiye"
            };
        }
    },
    US: {
        generate: () => {
            const stateData = getRandomItem(US_STATES);
            const city = getRandomItem(stateData.cities);
            const street = getRandomItem(US_STREETS);
            const number = getRandomNumber(100, 9999);
            const zip = getRandomNumber(10000, 99999);

            return {
                fullAddress: `${number} ${street}\n${city}, ${stateData.abr} ${zip}`,
                city,
                state: stateData.name,
                street: `${number} ${street}`,
                zip,
                country: "United States"
            };
        }
    },
    DE: {
        generate: () => {
            const cityData = getRandomItem(DE_CITIES);
            const street = getRandomItem(DE_STREETS);
            const number = getRandomNumber(1, 150);
            const zip = getRandomItem(cityData.zips);

            return {
                fullAddress: `${street} ${number}\n${zip} ${cityData.name}`,
                city: cityData.name,
                street: `${street} ${number}`,
                zip,
                country: "Deutschland"
            };
        }
    },
    UK: {
        generate: () => {
            const city = getRandomItem(UK_CITIES);
            const street = getRandomItem(UK_STREETS);
            const number = getRandomNumber(1, 100);
            // Simple UK Postcode generator logic
            const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
            const pc1 = chars[getRandomNumber(0, 25)] + chars[getRandomNumber(0, 25)];
            const pc2 = getRandomNumber(1, 99) + " " + getRandomNumber(1, 9) + chars[getRandomNumber(0, 25)] + chars[getRandomNumber(0, 25)];
            const postcode = pc1 + pc2;

            return {
                fullAddress: `${number} ${street}\n${city}\n${postcode}`,
                city,
                street: `${number} ${street}`,
                zip: postcode,
                country: "United Kingdom"
            };
        }
    }
};

export const AVAILABLE_COUNTRIES = [
    { code: "TR", name: "Turkey" },
    { code: "US", name: "United States" },
    { code: "DE", name: "Germany" },
    { code: "UK", name: "United Kingdom" },
];
